import { forwardRef } from 'react';

import { cn } from 'components/utils';

import { dividerRecipe } from './Divider.recipe';

import type { DividerProps } from './types';

/**
 * Design System 2.0 divider — the 1px rule that separates groups of content.
 *
 * Ported from the `Divider` component set (file 5aSkE0U4TeWbuIZDvccHjc, node 1472:150575). One
 * variant axis, `Orientation`, one drawn layer, and one bound colour. Two things about the port are
 * worth knowing before changing anything:
 *
 *   - the rule is FULL-BLEED, which follows the set's description and deviates from its drawing.
 *     `Divider.recipe` has the argument and the measurement;
 *   - the colour is a translucent overlay, which is what lets one divider work on the page, in a
 *     panel and in a menu. The `.ds-divider` block in `design-system.css` has the numbers.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer, so
 * the same markup is correct in both themes and `.ds-divider` needs no dark rule to achieve it.
 *
 * ── What it is, and what it is not ────────────────────────────────────────────────────────────────
 *
 * It is a rule. It carries no label and no weight axis, and that is the set's decision rather than
 * an unfinished port — its description names both and rules both out. So the three embedded login
 * forms that render `<Divider flexItem>or</Divider>` in MUI cannot move to this component yet, and
 * `DividerProps` refuses `children` so that they cannot do it by accident.
 *
 * ── No Base UI primitive behind it ────────────────────────────────────────────────────────────────
 *
 * `Tag` and `ContentPanel` are the precedent: a styled element with no headless base, because there
 * is no behaviour to keep off. A divider has no state, no keyboard model and no focus to manage.
 * What it does have is SEMANTICS, and those are two attributes rather than a dependency.
 *
 * ── It announces itself, unlike `ContentPanel` ────────────────────────────────────────────────────
 *
 * `role="separator"` by default, where the panel next door deliberately renders a bare `<div>` and
 * leaves its role to the caller. The two answers come from the same question asked of different
 * components: what does the element mean if nobody configures it?
 *
 * A panel with no role means nothing — a `region` with no accessible name is not exposed as one, so
 * a default role there would be inert at best and a surprise landmark at worst. A divider's meaning
 * is the whole component: it is the boundary between two groups, it is what `role="separator"` says,
 * and a non-focusable separator needs no name to be useful. It is also what the legacy MUI
 * `<Divider>` these call sites are migrating from already announced, via its `<hr>`.
 *
 * `aria-orientation` is set only on the vertical cell. ARIA's own default for `separator` is
 * horizontal, so stating it there would be a redundant attribute rather than a clearer one.
 *
 * A caller whose rule is genuinely redundant — one that only restates a gap the layout already
 * shows — turns it off with `role="presentation"`, which the open props type allows and the unit
 * test pins. That is the right way round: the common case needs no thought, and the exception is one
 * explicit attribute at the call site.
 *
 * ── Length is the caller's, on the vertical cell ──────────────────────────────────────────────────
 *
 * A vertical divider stretches to its flex parent and has no height in any other kind of parent. See
 * `Divider.recipe`; it is the one thing about this component that surprises people.
 */
export const Divider = forwardRef<HTMLDivElement, DividerProps>(({ className, orientation, ...rest }, ref) => (
  <div
    ref={ref}
    aria-orientation={orientation === 'vertical' ? 'vertical' : undefined}
    className={cn(dividerRecipe({ orientation }), className)}
    role="separator"
    {...rest}
  />
));
